import { USER_KEY, api } from "../api/client";
import { fetchBlob } from "../api/files";
import { saveFile } from "./download";

/**
 * Shared links and the signups they bring (ADR 0016).
 *
 * A link someone shares carries `?ref=<their username>`. Whoever opens it
 * keeps the ref for the visit (`rememberRef`) and signs up with it
 * (`takeRef`), so the API can count the signups each share brings. Sharing
 * is reported as `profile_shared`, with the kind of page the link points at,
 * as `recap_shared` for a year in media, or as `take_shared` for someone's
 * take on one title (plan 018).
 *
 * A link can carry `?invite=<code>` too: a channel's (`bsky`) or someone's
 * own (plan 003 C.2). It's kept the same way, for the signup page to check
 * and send (`takeInvite`).
 */

export type SharedPage = "profile" | "list" | "work" | "recap" | "take";

const REF_KEY = "khipur_ref";
const INVITE_KEY = "khipur_invite";

// The signed-in user's name, as AuthContext last stored it.
const sessionUsername = (): string | null => {
  try {
    return (
      JSON.parse(localStorage.getItem(USER_KEY) ?? "null")?.username ?? null
    );
  } catch {
    return null;
  }
};

/** The full link to `path`, carrying the signed-in sharer as its ref. */
export const shareUrl = (path: string): string => {
  const url = new URL(path, window.location.origin);
  const username = sessionUsername();
  if (username) url.searchParams.set("ref", username);
  return url.toString();
};

// Only a signed-in sharer can be counted; losing one is fine. A page the
// API has no name for yet (null) goes uncounted but for its ref.
const report = (page: SharedPage | null): void => {
  if (page && sessionUsername())
    void api
      .post(
        "/events",
        page === "recap" || page === "take"
          ? { name: `${page}_shared` }
          : { name: "profile_shared", page },
      )
      .catch(() => undefined);
};

// Copy the link to `path`; resolves to whether it was copied.
const copyLink = async (
  path: string,
  page: SharedPage | null,
): Promise<boolean> => {
  if (!navigator.clipboard) return false;
  await navigator.clipboard.writeText(shareUrl(path));
  report(page);
  return true;
};

/** Copy a link to share; resolves to whether it was copied. */
export const copyShareLink = (
  path: string,
  page: SharedPage,
): Promise<boolean> => copyLink(path, page);

// The image at `url` as a file named `name`, to share, or null where the
// device can't share one or it can't be fetched.
const cardFile = async (url: string, name: string): Promise<File | null> => {
  if (typeof navigator.canShare !== "function") return null;
  try {
    const file = new File([await fetchBlob(url)], name, {
      type: "image/png",
    });
    return navigator.canShare({ files: [file] }) ? file : null;
  } catch {
    return null;
  }
};

// Share the link to `path` through the device's share sheet, with the
// image `card` names as the post where the device can share one; else
// copy the link. Resolves to "shared", "copied", or null when nothing
// happened (the sheet was dismissed, or there's no clipboard).
const share = async (
  path: string,
  page: SharedPage | null,
  title: string,
  card?: { url: string; name: string },
): Promise<"shared" | "copied" | null> => {
  if (typeof navigator.share !== "function")
    return (await copyLink(path, page)) ? "copied" : null;
  const file = card ? await cardFile(card.url, card.name) : null;
  try {
    await navigator.share(
      file
        ? { files: [file], title, url: shareUrl(path) }
        : { title, url: shareUrl(path) },
    );
  } catch {
    return null;
  }
  report(page);
  return "shared";
};

/**
 * Share a link through the device's share sheet where there is one, else
 * copy it. Resolves to "shared", "copied", or null when nothing happened
 * (the sheet was dismissed, or there's no clipboard).
 */
export const shareLink = (
  path: string,
  page: SharedPage,
  title: string,
): Promise<"shared" | "copied" | null> => share(path, page, title);

/**
 * Share a page with one of its cards (`card`: its address, and the file's
 * name), as the share preview (SharePreview) picked it: on a phone, the
 * share sheet with the card as the post; elsewhere as `shareLink` does. A
 * page the API has no name for (null), as a diary's, counts only the
 * signups its ref brings. Resolves as `shareLink` does.
 */
export const shareCard = (
  path: string,
  page: SharedPage | null,
  title: string,
  card: { url: string; name: string },
): Promise<"shared" | "copied" | null> => share(path, page, title, card);

/**
 * Share someone's take on one title (plan 018): on a phone, the share sheet
 * with its portrait image (`post.png`), the image being the post; elsewhere
 * as `shareLink` does. Resolves as `shareLink` does.
 */
export const shareTake = (
  path: string,
  title: string,
): Promise<"shared" | "copied" | null> =>
  share(path, "take", title, {
    url: `${path}/post.png`,
    name: "khipur-take.png",
  });

/**
 * Save a card's image (`card`: its address, and the file's name), as
 * `saveFile` does: from an iPhone's home screen through the share sheet,
 * elsewhere a download. Resolves as `saveFile` does, or null when the image
 * couldn't be fetched.
 */
export const saveCard = async (card: {
  url: string;
  name: string;
}): Promise<"shared" | "saved" | null> => {
  let file: File;
  try {
    file = new File([await fetchBlob(card.url)], card.name, {
      type: "image/png",
    });
  } catch {
    return null;
  }
  return saveFile(file);
};

const keep = (key: string, value: string | null): void => {
  if (!value) return;
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked: the signup just goes uncounted.
  }
};

const take = (key: string): string | undefined => {
  try {
    return sessionStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

/** Keep a link's `?ref=` and `?invite=` for this visit, so a signup can name them. */
export const rememberRef = (search: string): void => {
  const params = new URLSearchParams(search);
  keep(REF_KEY, params.get("ref"));
  keep(INVITE_KEY, params.get("invite"));
};

/** The ref this visit arrived with, if any. */
export const takeRef = (): string | undefined => take(REF_KEY);

/** The invite code this visit arrived with, if any. */
export const takeInvite = (): string | undefined => take(INVITE_KEY);

/**
 * Keep an invite code typed in for this visit, so a signup that leaves for
 * Google or Apple and comes back can still name it.
 */
export const keepInvite = (code: string | undefined): void =>
  keep(INVITE_KEY, code ?? null);
